<template>
  <!--
    POPUP GLOBAL DE MENSAGENS
    Montado UMA vez no App.vue, então aparece em qualquer tela.

    Decisões de UX:
    1. NÃO MODAL: não escurece nem bloqueia a tela. O operador pode estar no
       meio de uma checagem de medicamento. Bloquear a tela gera pressa e erro.
    2. NÃO ROUBA O FOCO: se o popup puxasse o foco enquanto alguém digita uma
       evolução, o próximo "Enter" confirmaria a leitura sem querer.
    3. PERSISTENTE: só some com "Entendi" (lida) ou "Lembrar depois" (adiada).
       Um toast que some sozinho seria perdido por quem está longe do PC.
    4. CANTO INFERIOR DIREITO: fora da área principal de trabalho, mas dentro
       do campo de visão. Padrão conhecido de chats (reduz aprendizado).
  -->
  <transition name="chat-popup-fade">
    <section
      v-if="visible"
      id="chat-incoming-popup"
      class="chat-popup"
      role="dialog"
      aria-modal="false"
      aria-labelledby="chat-popup-title"
      tabindex="-1"
      @keydown.esc.stop="remindLater"
    >
      <!-- ── Cabeçalho ─────────────────────────────────────────────── -->
      <header class="chat-popup__header">
        <i
          class="fas fa-comments chat-popup__header-icon"
          aria-hidden="true"
        ></i>

        <h2 id="chat-popup-title" class="chat-popup__title">{{ title }}</h2>

        <!-- Ícone + texto: "lápis" sozinho é ambíguo -->
        <button type="button" class="chat-popup__new-btn" @click="newMessage">
          <i class="fas fa-pen" aria-hidden="true"></i>
          Nova
        </button>

        <button
          type="button"
          class="chat-popup__icon-btn"
          :aria-pressed="chat.soundEnabled ? 'true' : 'false'"
          :aria-label="
            chat.soundEnabled ? 'Desligar som de aviso' : 'Ligar som de aviso'
          "
          :title="chat.soundEnabled ? 'Som ligado' : 'Som desligado'"
          @click="toggleSound"
        >
          <i
            :class="[
              'fas',
              chat.soundEnabled ? 'fa-volume-up' : 'fa-volume-mute',
            ]"
            aria-hidden="true"
          ></i>
        </button>

        <button
          type="button"
          class="chat-popup__icon-btn"
          :aria-label="total ? 'Fechar e lembrar em 5 minutos' : 'Fechar'"
          :title="total ? 'Fechar e lembrar em 5 minutos' : 'Fechar'"
          @click="remindLater"
        >
          <i class="fas fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <!-- ── Lista de mensagens ────────────────────────────────────── -->
      <ol v-if="total" class="chat-popup__list">
        <li
          v-for="message in chat.messages"
          :key="message.id"
          class="chat-popup__item"
        >
          <div class="chat-popup__meta">
            <span class="chat-popup__sender">{{ message.sender_name }}</span>

            <span
              :class="[
                'chat-popup__tag',
                message.is_broadcast
                  ? 'chat-popup__tag--all'
                  : 'chat-popup__tag--me',
              ]"
            >
              <i
                :class="['fas', message.is_broadcast ? 'fa-users' : 'fa-user']"
                aria-hidden="true"
              ></i>
              {{ message.is_broadcast ? "Para todos" : "Para você" }}
            </span>
          </div>

          <p class="chat-popup__body">{{ message.body }}</p>

          <div class="chat-popup__footer-line">
            <time class="chat-popup__time" :datetime="message.created_at">
              {{ formatWhen(message.created_at) }}
            </time>

            <!-- Aviso: texto veio de voz e pode ter erro de transcrição -->
            <span
              v-if="message.origin === 'VOICE'"
              class="chat-popup__voice"
              title="Mensagem gravada por voz e convertida em texto"
            >
              <i class="fas fa-microphone" aria-hidden="true"></i>
              Transcrita de voz
            </span>

            <span class="chat-popup__item-actions">
              <button
                type="button"
                class="chat-popup__link-btn"
                :aria-label="`Responder a ${message.sender_name}`"
                @click="reply(message)"
              >
                <i class="fas fa-reply" aria-hidden="true"></i>
                Responder
              </button>

              <button
                v-if="canSpeak"
                type="button"
                class="chat-popup__link-btn"
                :aria-label="`Ouvir mensagem de ${message.sender_name}`"
                @click="speak(message)"
              >
                <i
                  :class="[
                    'fas',
                    speakingId === message.id ? 'fa-stop' : 'fa-play',
                  ]"
                  aria-hidden="true"
                ></i>
                {{ speakingId === message.id ? "Parar" : "Ouvir" }}
              </button>
            </span>
          </div>
        </li>
      </ol>

      <!-- ── Estado vazio (aberto pelo sininho sem mensagens) ──────── -->
      <div v-else class="chat-popup__empty">
        <i class="fas fa-check-circle" aria-hidden="true"></i>
        <p>Nenhuma mensagem nova.</p>
        <button
          type="button"
          class="chat-popup__btn chat-popup__btn--primary"
          @click="newMessage"
        >
          <i class="fas fa-pen" aria-hidden="true"></i>
          Escrever mensagem
        </button>
      </div>

      <!-- ── Ações ─────────────────────────────────────────────────── -->
      <footer v-if="total" class="chat-popup__actions">
        <!--
          Botão secundário à esquerda e primário à direita, largo.
          A ação principal ("Entendi") é a maior: Lei de Fitts.
        -->
        <button
          type="button"
          class="chat-popup__btn chat-popup__btn--secondary"
          :disabled="chat.marking"
          @click="remindLater"
        >
          <i class="far fa-clock" aria-hidden="true"></i>
          Lembrar em 5 min
        </button>

        <button
          type="button"
          class="chat-popup__btn chat-popup__btn--primary"
          :disabled="chat.marking"
          @click="confirmRead"
        >
          <i
            :class="['fas', chat.marking ? 'fa-spinner fa-spin' : 'fa-check']"
            aria-hidden="true"
          ></i>
          {{ confirmLabel }}
        </button>
      </footer>

      <!-- Acesso ao histórico completo (Parte 4) -->
      <div class="chat-popup__history">
        <button
          type="button"
          class="chat-popup__history-link"
          @click="openHistory"
        >
          <i class="fas fa-history" aria-hidden="true"></i>
          Ver histórico de mensagens
        </button>
      </div>

      <!-- Anúncio para leitor de tela quando chega mensagem nova -->
      <p class="chat-popup__sr" role="status" aria-live="polite">
        {{ announcement }}
      </p>
    </section>
  </transition>
</template>

<script>
import moment from "moment";

import chatService from "../../services/chatService";

export default {
  name: "ChatIncomingPopup",

  data() {
    return {
      chat: chatService.state,
      speakingId: null,
      announcement: "",
      canSpeak: typeof window !== "undefined" && "speechSynthesis" in window,
    };
  },

  computed: {
    // Esconde enquanto o compositor está aberto (evita sobreposição ao modal)
    visible() {
      return this.chat.popupOpen && !this.chat.composer.open;
    },

    total() {
      return this.chat.messages.length;
    },

    title() {
      if (this.total === 0) return "Mensagens da equipe";
      if (this.total === 1) return "Nova mensagem da equipe";
      return `${this.total} novas mensagens da equipe`;
    },

    // Rótulo diz exatamente o que acontece ao clicar (evita surpresa)
    confirmLabel() {
      return this.total === 1 ? "Entendi" : `Entendi, li as ${this.total}`;
    },
  },

  watch: {
    // Anuncia a mensagem mais recente quando a lista cresce
    total(newTotal, oldTotal) {
      if (newTotal > oldTotal) {
        const last = this.chat.messages[newTotal - 1];
        this.announcement = last
          ? `Nova mensagem de ${last.sender_name}`
          : "Nova mensagem";
      }
    },
  },

  beforeDestroy() {
    this.stopSpeaking();
  },

  methods: {
    formatWhen(date) {
      const m = moment(date);
      if (!m.isValid()) return "";
      if (m.isSame(moment(), "day")) return `Hoje, ${m.format("HH:mm")}`;
      if (m.isSame(moment().subtract(1, "day"), "day"))
        return `Ontem, ${m.format("HH:mm")}`;
      return m.format("DD/MM/YYYY, HH:mm");
    },

    async confirmRead() {
      this.stopSpeaking();
      // Marca só o que está na tela neste momento
      const ids = this.chat.messages.map((m) => m.id);
      await chatService.markAsRead(ids);
    },

    remindLater() {
      this.stopSpeaking();
      chatService.snooze();
    },

    // Ir ao histórico não conta como leitura: o popup é só adiado
    openHistory() {
      this.stopSpeaking();
      chatService.snooze();
      if (this.$route && this.$route.name === "chat-history") return;
      this.$router.push({ name: "chat-history" }).catch(() => {});
    },

    newMessage() {
      this.stopSpeaking();
      chatService.openComposer();
    },

    // Responder: destinatário já vem preenchido (menos passos, menos erro)
    reply(message) {
      this.stopSpeaking();
      chatService.openComposer({ replyTo: message });
    },

    toggleSound() {
      chatService.setSound(!this.chat.soundEnabled);
    },

    // Leitura em voz alta: ajuda cuidadores com baixa visão ou pouca leitura
    speak(message) {
      if (!this.canSpeak) return;

      if (this.speakingId === message.id) {
        this.stopSpeaking();
        return;
      }

      window.speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(
        `Mensagem de ${message.sender_name}. ${message.body}`
      );
      utterance.lang = "pt-BR";
      utterance.rate = 1;
      utterance.onend = () => (this.speakingId = null);
      utterance.onerror = () => (this.speakingId = null);

      this.speakingId = message.id;
      window.speechSynthesis.speak(utterance);
    },

    stopSpeaking() {
      if (this.canSpeak) window.speechSynthesis.cancel();
      this.speakingId = null;
    },
  },
};
</script>

<style scoped>
/*
  Tokens locais (não alteram os CSS globais do projeto).
  Azul do sistema #0f65b0 mantém a identidade visual.
*/
.chat-popup {
  --cp-primary: #0f65b0;
  --cp-primary-dark: #0b4f8a;
  --cp-text: #1e293b;
  --cp-text-muted: #475569; /* 7.5:1 sobre branco */
  --cp-border: #e2e8f0;
  --cp-surface: #ffffff;
  --cp-surface-alt: #f8fafc;
  --cp-tag-me-bg: #dbeafe;
  --cp-tag-me-text: #1e3a8a;
  --cp-tag-all-bg: #fef3c7;
  --cp-tag-all-text: #78350f;
  --cp-radius: 16px;

  position: fixed;
  right: 24px;
  bottom: 24px;
  /* acima da navbar (1000) e abaixo dos modais do Bootstrap (1050),
     para não cobrir diálogos de confirmação */
  z-index: 1045;

  display: flex;
  flex-direction: column;
  width: 400px;
  max-width: calc(100vw - 32px);
  max-height: min(70vh, 640px);

  background: var(--cp-surface);
  border: 1px solid var(--cp-border);
  border-top: 4px solid var(--cp-primary);
  border-radius: var(--cp-radius);
  box-shadow: 0 16px 48px rgba(15, 23, 42, 0.22);
  color: var(--cp-text);
  font-size: 16px;
  overflow: hidden;
}

.chat-popup:focus {
  outline: none;
}

.chat-popup:focus-visible {
  box-shadow: 0 0 0 3px rgba(15, 101, 176, 0.45),
    0 16px 48px rgba(15, 23, 42, 0.22);
}

/* ── Cabeçalho ───────────────────────────────────────────────────────── */
.chat-popup__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 12px 12px 16px;
  background: var(--cp-surface-alt);
  border-bottom: 1px solid var(--cp-border);
}

.chat-popup__header-icon {
  color: var(--cp-primary);
  font-size: 20px;
}

.chat-popup__title {
  flex: 1;
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--cp-text);
}

.chat-popup__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: var(--cp-text-muted);
  font-size: 18px;
  cursor: pointer;
}

.chat-popup__icon-btn:hover {
  background: #e2e8f0;
  color: var(--cp-text);
}

/* ── Lista ───────────────────────────────────────────────────────────── */
.chat-popup__list {
  flex: 1;
  margin: 0;
  padding: 8px 16px;
  list-style: none;
  overflow-y: auto;
}

.chat-popup__item {
  padding: 12px 0;
  border-bottom: 1px solid var(--cp-border);
}

.chat-popup__item:last-child {
  border-bottom: 0;
}

.chat-popup__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.chat-popup__sender {
  font-size: 16px;
  font-weight: 700;
  color: var(--cp-text);
}

.chat-popup__tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.chat-popup__tag--me {
  background: var(--cp-tag-me-bg);
  color: var(--cp-tag-me-text);
}

.chat-popup__tag--all {
  background: var(--cp-tag-all-bg);
  color: var(--cp-tag-all-text);
}

.chat-popup__body {
  margin: 0 0 8px;
  font-size: 16px;
  line-height: 1.5;
  white-space: pre-wrap; /* respeita quebras de linha digitadas */
  word-break: break-word;
}

.chat-popup__footer-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  color: var(--cp-text-muted);
}

.chat-popup__voice {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.chat-popup__new-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 40px;
  padding: 0 12px;
  border: 1px solid var(--cp-primary);
  border-radius: 10px;
  background: #ffffff;
  color: var(--cp-primary);
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.chat-popup__new-btn:hover {
  background: #eff6ff;
}

.chat-popup__item-actions {
  display: inline-flex;
  gap: 6px;
  margin-left: auto;
}

.chat-popup__link-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 4px 10px;
  border: 1px solid var(--cp-border);
  border-radius: 8px;
  background: var(--cp-surface);
  color: var(--cp-primary);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.chat-popup__link-btn:hover {
  background: var(--cp-surface-alt);
}

/* ── Estado vazio ────────────────────────────────────────────────────── */
.chat-popup__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 16px;
  color: var(--cp-text-muted);
}

.chat-popup__empty i {
  font-size: 28px;
  color: #16a34a;
}

.chat-popup__empty p {
  margin: 0 0 8px;
}

/* ── Ações ───────────────────────────────────────────────────────────── */
.chat-popup__actions {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 8px;
  padding: 12px 16px 16px;
  border-top: 1px solid var(--cp-border);
  background: var(--cp-surface);
}

.chat-popup__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 48px;
  padding: 0 16px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.chat-popup__btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.chat-popup__btn--primary {
  border: 0;
  background: var(--cp-primary);
  color: #ffffff;
}

.chat-popup__btn--primary:hover:not(:disabled) {
  background: var(--cp-primary-dark);
}

.chat-popup__btn--secondary {
  border: 1px solid #cbd5e1;
  background: var(--cp-surface);
  color: var(--cp-text);
}

.chat-popup__btn--secondary:hover:not(:disabled) {
  background: var(--cp-surface-alt);
}

.chat-popup__history {
  padding: 8px 16px 12px;
  border-top: 1px solid var(--cp-border);
  text-align: center;
}

.chat-popup__history-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 0 8px;
  border: 0;
  background: transparent;
  color: var(--cp-primary);
  font-size: 14px;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
}

/* Foco visível em todos os botões */
.chat-popup button:focus {
  outline: none;
}

.chat-popup button:focus-visible {
  outline: 3px solid rgba(15, 101, 176, 0.55);
  outline-offset: 2px;
}

/* Texto apenas para leitor de tela */
.chat-popup__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* ── Entrada / saída ─────────────────────────────────────────────────── */
.chat-popup-fade-enter-active,
.chat-popup-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.chat-popup-fade-enter,
.chat-popup-fade-leave-to {
  opacity: 0;
  transform: translateY(16px);
}

@media (prefers-reduced-motion: reduce) {
  .chat-popup-fade-enter-active,
  .chat-popup-fade-leave-active {
    transition: none;
  }
}

/* ── Celular / tablet estreito: vira painel inferior de largura total ── */
@media (max-width: 576px) {
  .chat-popup {
    right: 8px;
    bottom: 8px;
    left: 8px;
    width: auto;
    max-width: none;
    max-height: 80vh;
  }

  .chat-popup__actions {
    grid-template-columns: 1fr;
  }

  /* Primário em cima no celular: mais perto do polegar e da leitura */
  .chat-popup__btn--primary {
    order: -1;
  }
}
</style>